import { DependencyList, useEffect, useRef, useState } from 'react';

type Axis = 'horizontal' | 'vertical';

/**
 * Tracks whether an element's content is clipped by `truncate` (horizontal) or
 * `line-clamp` (vertical). Measured after layout and re-measured on resize.
 *
 * Attach the returned `ref` to the clipped element; pass the values it depends on
 * (e.g. the text) as `deps` so it re-measures when they change.
 */
export const useIsTruncated = <T extends HTMLElement = HTMLElement>(
  axis: Axis = 'horizontal',
  deps: DependencyList = []
) => {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const update = () =>
      setIsTruncated(
        axis === 'vertical' ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth
      );
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axis, ...deps]);

  return { ref, isTruncated };
};
